// Module Name for TSDocs
/**
 * [[include:tracking.md]]
 * @module Tracking
 */

import {
  BffTrimData,
  ChargeTimeTrimModel,
  CostTrimModel,
  RangeSimRimData,
  RangeSimRimId,
  RangeTrimModel,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {
  ChargingLocationPrices,
  ChargingLocationRatio,
  FuelPrices,
} from '../components/ratio-selection';

interface TrackingPayloadMap {
  [TrackingEvent.FeatureAppLoaded]: Record<string, never>;
  [TrackingEvent.EducationalInfoClick]: EducationalInfoClickEventPayload;
  [TrackingEvent.ModelEfficiencyDataLayerOpen]: ModelEfficiencyDataLayerOpenEventPayload;
  [TrackingEvent.DrivingProfileChange]: DrivingProfileChangeEventPayload;
  [TrackingEvent.PayloadToggleChange]: PayloadToggleChangeEventPayload;
  [TrackingEvent.ExactPayloadChange]: ExactPayloadChangeEventPayload;
  [TrackingEvent.HighwayToggleChange]: HighwayToggleChangeEventPayload;
  [TrackingEvent.HacToggleChange]: HacToggleChangeEventPayload;
  [TrackingEvent.OutsideTemperatureChange]: OutsideTemperatureChangeEventPayload;
  [TrackingEvent.ChargingLocationChange]: ChargingLocationChangeEventPayload;
  [TrackingEvent.ChargerTypeChange]: ChargerTypeChangeEventPayload;
  [TrackingEvent.ChargingPowerChange]: ChargingPowerChangeEventPayload;
  [TrackingEvent.ChargingLocationRatioChange]: ChargingLocationRatioChangeEventPayload;
  [TrackingEvent.ChargingLocationPriceChange]: ChargingLocationPriceChangeEventPayload;
  [TrackingEvent.ChargingLocationRatioResetButtonClick]: ChargingLocationRatioChangeEventPayload;
  [TrackingEvent.FuelPriceChange]: FuelPriceChangeEventPayload;
  [TrackingEvent.ChargeDeltaChange]: ChargeDeltaChangeEventPayload;
  [TrackingEvent.AnnualDistanceChange]: AnnualDistanceChangeEventPayload;
  [TrackingEvent.RangeTrimSelect]: RangeTrimSelectEventPayload;
  [TrackingEvent.RangeEngineSelect]: RangeEngineSelectEventPayload;
  [TrackingEvent.RangeTireTypeSelect]: RangeTireTypeSelectEventPayload;
  [TrackingEvent.RangeRimSelect]: RangeRimSelectEventPayload;
  [TrackingEvent.ChargeTimeTrimSelect]: ChargeTimeTrimSelectEventPayload;
  [TrackingEvent.ChargeTimeEngineSelect]: ChargeTimeEngineSelectEventPayload;
  [TrackingEvent.CostTrimSelect]: CostTrimSelectEventPayload;
  [TrackingEvent.CostEngineSelect]: CostEngineSelectEventPayload;
  [TrackingEvent.WeChargeTariffChange]: CarbonOffsetToggleChangeEventPayload;
  [TrackingEvent.CarbonOffsetChange]: CarbonOffsetToggleChangeEventPayload;
  [TrackingEvent.ElliWallboxExternalLinkClick]: ElliWallboxExternalLinkClickEventPayload;
  [TrackingEvent.TariffMoreInfoButtonClick]: TariffMoreInfoButtonClick;
  [TrackingEvent.TariffPdfDownloadButtonClick]: TariffPdfDownloadButtonClick;
  [TrackingEvent.PreHeatedBattery]: PreHeatedBatteryToggleChangeEventPayload;
  [TrackingEvent.LayerButtonOpenClick]: LayerButtonTrackingPayload;
  [TrackingEvent.LayerButtonFeatureAppTabClick]: LayerButtonTrackingPayload;
  [TrackingEvent.BasicButtonClick]: BasicButtonClickTrackingPayload;
}

export interface TrackingLinkClickEventData {
  name: string;
  url?: string;
}

export interface TrackingDownloadPdfClickEventData {
  name: string;
  type: string;
  language: string;
  productName?: string;
  category?: string;
  url?: string;
}

export type TrackingFunctions<Ctx> = Partial<
  {
    [T in TrackingEvent]?: (payload: TrackingPayloadMap[T], ctx: Ctx) => void;
  }
>;

/**
 * Type that can be used to type tracking functions, if they do not depend on
 * the feature-app-specific ctx value.
 */
export type ContextLessTrackingFunctions = Partial<
  {
    [T in TrackingEvent]?: (payload: TrackingPayloadMap[T]) => void;
  }
>;

export enum TrackingEvent {
  /**
   * This event is triggered as soon as all data is available and the feature
   * app fully rendered for the firs time.
   *
   * This event currently has no payload (i.e. it is an empty object)
   *
   * @category All Apps
   */
  FeatureAppLoaded = 'FeatureAppLoaded',

  /**
   * This event is triggered in the Range Simulator, whenever a user clicks on
   * one of the "educational info" icons next to e.g. a Form Question.
   *
   * The "click" might also be triggered by a keyboard.
   *
   * See [[EducationalInfoClickEventPayload]]
   *
   * @category All Apps
   */
  EducationalInfoClick = 'EducationalInfoClick',

  /**
   * This event is triggered in the ChargeTime Simulator, whenever a user clicks on
   * the "external link for elli wallbox".
   *
   * The "click" might also be triggered by a keyboard.
   *
   * See [[ElliWallboxExternalLinkClickEventPayload]]
   *
   * @category All Apps
   */
  ElliWallboxExternalLinkClick = 'ElliWallboxExternalLinkClick',

  /**
   * This event is triggered then the user clicks on the button to open the
   * layer containing all the efficiency data of the shown model.
   *
   * This event currently has no payload (i.e. it is an empty object)
   *
   * @category All Apps
   */
  ModelEfficiencyDataLayerOpen = 'ModelEfficiencyDataLayerOpen',

  /**
   * This event is triggered in the Range and Cost Simulator, whenever the
   * user selects a new percentage distribution for the available driving
   * locations.
   *
   * See [[DrivingProfileChangeEventPayload]]
   *
   * @category Range
   * @category Cost
   */
  DrivingProfileChange = 'DrivingProfileChange',

  /**
   * This event is triggered in the private(!) Range Simulator, whenever the
   * toggle switching between low or high amounts of payload is toggled by the
   * user.
   *
   * See [[PayloadToggleChangeEventPayload]]
   *
   * @category Range
   */
  PayloadToggleChange = 'PayloadToggleChange',

  /**
   * This event is triggered in the commercial (!) Range Simulator, whenever the
   * selection for the payload weight is adjusted by the user.
   *
   * See [[ExactPayloadChangeEventPayload]]
   *
   * @category Range
   */
  ExactPayloadChange = 'ExactPayloadChange',

  /**
   * This event is triggered in the private(!) Range Simulator, whenever the
   * optionally configured toggle to switch between normal or very fast
   * (>120 km/h) driving behaviour on the highway/motorway/Autobahn
   * (if even legal in that country).
   *
   * See [[HighwayToggleChangeEventPayload]]
   *
   * @category Range
   */
  HighwayToggleChange = 'HighwayToggleChange',

  /**
   * This event is triggered in the Range Simulator, whenever the toggle to
   * switch between HAC (Heating, Air Conditioning) on or off is toggled by the
   * user.
   *
   * Note that this does explicitly not track the automatic toggling based on
   * the temperature that can be set up with a feature flag.
   *
   * @category Range
   */
  HacToggleChange = 'HacToggleChange',

  /**
   * This event is triggered in the Range Simulator, whenever a user changes the
   * outside temperature (only after e.g. sliding is done!).
   *
   * See [[OutsideTemperatureChangeEventPayload]]
   *
   * @category Range
   * @category ChargeTime
   */
  OutsideTemperatureChange = 'OutsideTemperatureChange',

  /**
   * This event is triggered in the ChargeTime and Cost Simulators, whenever a
   * user changes the charging location.
   *
   * See [[ChargingLocationChangeEventPayload]]
   *
   * @category ChargeTime
   * @category Cost
   */
  ChargingLocationChange = 'ChargingLocationChange',

  /**
   * This event is triggered in the ChargeTime and Cost Simulators, whenever a
   * user changes the charger type for the charging location.
   *
   * See [[ChargerTypeChangeEventPayload]]
   *
   * @category ChargeTime
   * @category Cost
   */
  ChargerTypeChange = 'ChargerTypeChange',

  /**
   * This event is triggered in the ChargeTime and Cost Simulators, whenever a
   * user changes the charging power for the charging type.
   *
   * See [[ChargingPowerChangeEventPayload]]
   *
   * @category ChargeTime
   * @category Cost
   */
  ChargingPowerChange = 'ChargingPowerChange',

  /**
   * This event is triggered in the Cost Simulator, whenever a
   * user changes the charging location sliders.
   *
   * See [[ChargingLocationRatioChangeEventPayload]]
   *
   * @category ChargeTime
   * @category Cost
   */
  ChargingLocationRatioChange = 'ChargingLocationRatioChange',

  /**
   * This event is triggered in the Cost Simulator, whenever a
   * user changes the charging price.
   *
   *  * @category Cost
   */
  ChargingLocationPriceChange = 'ChargingLocationPriceChange',

  /**
   * This event is triggered in the Cost Simulator,
   * whenever user clicks on the reset button.
   *
   *  * @category Cost
   */
  ChargingLocationRatioResetButtonClick = 'ChargingLocationRatioResetButtonClick',

  /**
   * This event is triggered in the Cost Simulator, whenever a
   * user changes the fuel price.
   *
   *  * @category Cost
   */
  FuelPriceChange = 'FuelPriceChange',

  /**
   * This event is triggered in the Cost Simulators, whenever a
   * user changes the carbon offset.
   *
   *
   * @category Cost
   */
  CarbonOffsetChange = 'CarbonOffsetChange',

  /**
   * This event is triggered in the Cost Simulators, whenever a
   * user changes the we-charge tariffs toggle.
   *
   *
   * @category Cost
   */
  WeChargeTariffChange = 'WeChargeTariffChange',

  /**
   * This event is triggered in the ChargeTime Simulators, whenever a
   * user changes the preHeated battery toggle.
   *
   *
   * @category ChargeTime
   */
  PreHeatedBattery = 'PreHeatedBatteryChange',

  /**
   * This event is triggered in the ChargeTime Simulator, whenever a user
   * changes the "charging from x%" and "charging to y%" values.
   *
   * See [[ChargeDeltaChangeEventPayload]]
   *
   * @category ChargeTime
   */
  ChargeDeltaChange = 'ChargeDeltaChange',

  /**
   * This event is triggered in the Cost Simulator, whenever a user sets the
   * annual driven distance.
   *
   * See [[AnnualDistanceChangeEventPayload]]
   *
   * @category Cost
   */
  AnnualDistanceChange = 'AnnualDistanceChange',

  /**
   * This event is triggered in the Range Simulator, whenever a user selects a
   * trim from the "Model" selection.
   *
   * See [[RangeTrimSelectEventPayload]]
   *
   * @category Range
   */
  RangeTrimSelect = 'RangeTrimSelect',

  /**
   * Event triggered in the Range Simulator, whenever a user selects an
   * engine/specific model from the "Engine" selection.
   *
   * See [[RangeEngineSelectEventPayload]]
   *
   * @category Range
   */
  RangeEngineSelect = 'RangeEngineSelect',

  /**
   * This event is triggered in the Range Simulator, whenever a user selects a
   * tire type (summer or winter tires).
   *
   * See [[RangeTireTypeSelectEventPayload]]
   *
   * @category Range
   */
  RangeTireTypeSelect = 'RangeTireTypeSelect',

  /**
   * This event is triggered in the Range Simulator, whenever a user selects a
   * rim size.
   *
   * See [[RangeRimSelectEventPayload]]
   *
   * @category Range
   */
  RangeRimSelect = 'RangeRimSelect',

  /**
   * This event is triggered in the ChargeTime Simulator, whenever a user selects a
   * trim from the "Model" selection.
   *
   * See [[ChargeTimeTrimSelectEventPayload]]
   *
   * @category ChargeTime
   */
  ChargeTimeTrimSelect = 'ChargeTimeTrimSelect',

  /**
   * Event triggered in the ChargeTime Simulator, whenever a user selects an
   * engine/specific model from the "Engine" selection.
   *
   * See [[ChargeTimeEngineSelectEventPayload]]
   *
   * @category ChargeTime
   */
  ChargeTimeEngineSelect = 'ChargeTimeEngineSelect',

  /**
   * This event is triggered in the Cost Simulator, whenever a user selects a
   * trim from the "Model" selection.
   *
   * See [[CostTrimSelectEventPayload]]
   *
   * @category Cost
   */
  CostTrimSelect = 'CostTrimSelect',

  /**
   * Event triggered in the Cost Simulator, whenever a user selects an
   * engine/specific model from the "Engine" selection.
   *
   * See [[CostEngineSelectEventPayload]]
   *
   * @category Cost
   */
  CostEngineSelect = 'CostEngineSelect',
  /**
   * This event is triggered in the Tariffs FA, whenever user click
   * on a More Info button.
   *
   * @category Tariffs
   */
  TariffMoreInfoButtonClick = 'TariffMoreInfoButtonClick',
  /**
   * This event is triggered in the Tariffs FA, whenever user click
   * on a download PDF button.
   *
   * @category Tariffs
   */
  TariffPdfDownloadButtonClick = 'TariffPdfDownloadButtonClick',
  /**
   * This event is triggered in the Layer FA, whenever user click the button to open the layer.
   *
   * @category LayerButton
   */
  LayerButtonOpenClick = 'LayerButtonOpenClick',
  /**
   * This event is triggered in the Layer FA, whenever user clicks on a tab withing the layer to switch to another feature app.
   *
   * @category LayerButton
   */
  LayerButtonFeatureAppTabClick = 'LayerButtonFeatureAppTabClick',
  /**
   * The event triggered when configure button is clicked.
   *
   * @category Cost
   * @category Range
   * @category Chargetime
   */
  BasicButtonClick = 'BasicButtonClick',
}

export interface EducationalInfoClickEventPayload {
  /** The label for the clicked educational info button (translated) */
  label: string;
  link: TrackingLinkClickEventData;
}

export interface ModelEfficiencyDataLayerOpenEventPayload {
  /** The label for the clicked educational info button (translated) */
  label: string;
  link: TrackingLinkClickEventData;
}

export interface ElliWallboxExternalLinkClickEventPayload {
  /** The label for the clicked external link for Elli button (translated) */
  label: string;
  link: TrackingLinkClickEventData;
}

export interface TariffMoreInfoButtonClick {
  tariffName: string;
  tariffCtaLink: string;
}

export interface TariffPdfDownloadButtonClick {
  file: TrackingDownloadPdfClickEventData;
  link: TrackingLinkClickEventData;
}

export interface LayerButtonTrackingPayload {
  label: string;
  modelId?: string;
}

export interface BasicButtonClickTrackingPayload {
  /** The label for the clicked button or external link (translated) */
  label: string;
  link: TrackingLinkClickEventData;
}

export interface PayloadToggleChangeEventPayload {
  /**
   * The new value of the "payload" toggle in the private Range Simulator.
   * The old value can be accessed from Range's `ctx.appState.payloadPeople`.
   *
   * These values are called 'one' or 'multiple' based on an earlier design
   * of the Range Simulator that asked for "how many people are in the car".
   *
   * - `'one'` means a low payload / no passengers or cargo.
   * - `'multiple'` means a high payload / some passengers or cargo.
   */
  value: 'one' | 'multiple';
}

export interface ExactPayloadChangeEventPayload {
  /**
   * The new value of the "payload" slider in the commercial Range Simulator in
   * kg. The old value can be accessed from Range's
   * `ctx.appState.payloadWeight`.
   */
  payloadKg: number;
}

export interface HighwayToggleChangeEventPayload {
  /**
   * The new value of the "highway speed" toggle in the private Range Simulator.
   * The old value can be accessed from Range's `ctx.appState.fasterHighway`.
   *
   * - `'yes'` means the user does drive faster than 120 km/h on the highway.
   * - `'no'` means they do not.
   */
  value: 'yes' | 'no';
}

export interface HacToggleChangeEventPayload {
  /**
   * The new value of the "Heating/Air Conditioning" toggle in the Range
   * Simulator. The old value can be accessed from Range's
   * `ctx.appState.airConditioning`.
   *
   * - `'yes'` means the user does have the Heating or Air Conditioning on.
   * - `'no'` means they do not.
   */
  value: 'yes' | 'no';
}

export interface CarbonOffsetToggleChangeEventPayload {
  /**
   * The new value of the "Carbon offset" toggle in the Cost
   * Simulator. The old value can be accessed from Cost's
   * `ctx.appState.carbonOffset`.
   *
   * - `'yes'` means the user does have the carbon offset on
   * - `'no'` means they do not.
   */
  value: 'yes' | 'no';

  /**
   * The name of the toggle button
   */
  name: string;
}

export interface PreHeatedBatteryToggleChangeEventPayload {
  /**
   * The new value of the "PreHeated battery" toggle in the ChargeTime
   * Simulator. The old value can be accessed from Cost's
   * `ctx.appState.preHeatedBattery`.
   *
   * - `'yes'` means the user want to use preHeated battery data
   * - `'no'`  means the user want to use nonPreHeated battery data
   */
  value: 'yes' | 'no';

  /**
   * The name of the toggle button
   */
  name: string;
}

export interface OutsideTemperatureChangeEventPayload {
  /**
   * The newly selected temperature in °C. The old value can be accessed in
   * either Range's or ChargeTime's `ctx.formState.outsideTemperature`.
   */
  temperatureCelsius: number;
}

export interface DrivingProfileChangeEventPayload {
  /**
   * The newly selected percentages as integers. Will always add up to 100.
   * The old percentages can be accessed in either Range or Costs FA's `ctx.formState.drivingProfile`.
   */
  locationPercentages: [city: number, country: number, highway: number];
}

export interface ChargingLocationChangeEventPayload {
  /**
   * The newly chosen location in the charging location selection. The old
   * location can be accessed in ChargeTime's and Cost's
   * `ctx.formState.chargingLocation`.
   */
  location: 'atHome' | 'atWork' | 'atPublic' | 'atHighway';
}

export interface ChargerTypeChangeEventPayload {
  /**
   * The newly chosen type in the charging type selection. The old
   * type can be accessed in ChargeTime's and Cost's
   * `ctx.formState.chargerType`.
   */
  chargerType: 'socketAC' | 'wallboxAC' | 'stationDC';
}

export interface ChargingPowerChangeEventPayload {
  /**
   * The newly chosen charging power in the charging power selection. The old
   * charging power can be accessed in ChargeTime's and Cost's
   * `ctx.formState.chargingPower`.
   */
  chargingPower: '2.3' | '11' | '50' | '150' | '350' | '7.2' | '22' | '40';
}

export interface ChargingLocationRatioChangeEventPayload {
  /**
   * The newly chosen location ratio for the charging location selection.
   */
  location: ChargingLocationRatio;
}

export interface ChargingLocationPriceChangeEventPayload {
  /**
   * The newly typed value for the charging price.
   */
  price: ChargingLocationPrices;
}

export interface FuelPriceChangeEventPayload {
  /**
   * The newly typed value for the fuel price.
   */
  price: FuelPrices;
}

export interface ChargeDeltaChangeEventPayload {
  /**
   * The newly chosen "from" and "to" percentage values in the charge selection.
   * The old percentages can be accessed in ChargeTime's
   * `ctx.formState.chargeDelta`.
   */
  percentages: [from: number, to: number];
}

export interface AnnualDistanceChangeEventPayload {
  /**
   * The newly chosen annual distance in km. The old value can be accessed from
   * Cost's `ctx.formState.annualDistance`.
   */
  distanceKm: number;
}

export interface RangeTrimSelectEventPayload {
  /**
   * The trim dataset of the new trim that was selected in the Range Simulator
   * The old value can be accessed from Range's `ctx.selectedTrim`.
   */
  trim: BffTrimData<RangeTrimModel>;
  /**
   * The model dataset of the new model that was automatically selected by
   * changing the trim in the Range Simulator. The old value can be accessed
   * from Range's `ctx.selectedModel`.
   */
  model: RangeTrimModel;
  /**
   * The tire type that was automatically selected by changing the trim in the
   * Range Simulator. The old value can be accessed from Range's
   * `ctx.formState.tireType`.
   */
  tireType: 'summer' | 'winter' | 'allSeason';
  /**
   * The rim ID of the rim size that was automatically selected for the new
   * trim / model / tire type in the Range Simulator. The old value can be
   * accessed from Range's `ctx.formState.rim`.
   */
  rimId: RangeSimRimId;
  /**
   * The rim dataset of the rim size that was automatically selected for the
   * new trim / model / tire type in the Range Simulator. The old value can be
   * accessed from Range's `ctx.selectedRim`.
   */
  rim: RangeSimRimData;
}

export interface RangeEngineSelectEventPayload {
  /**
   * The model dataset of the new engine/model that was selected in the Range
   * Simulator. The old value can be accessed from Range's `ctx.selectedModel`.
   */
  model: RangeTrimModel;
  /**
   * The tire type that was automatically selected for the new model in the
   * Range Simulator. The old value can be accessed from Range's
   * `ctx.formState.tireType`.
   */
  tireType: 'summer' | 'winter' | 'allSeason';
  /**
   * The rim ID of the rim size that was automatically selected for the new
   * model and tire type in the Range Simulator. The old value can be accessed
   * from Range's `ctx.formState.rim`.
   */
  rimId: RangeSimRimId;
  /**
   * The rim dataset of the rim size that was automatically selected for the
   * new model and tire type in the Range Simulator. The old value can be
   * accessed from Range's `ctx.selectedRim`.
   */
  rim: RangeSimRimData;
}

export interface RangeTireTypeSelectEventPayload {
  /**
   * The new tire type that was selected in the Range Simulator. The old value
   * can be accessed from Range's `ctx.formState.tireType`.
   */
  tireType: 'summer' | 'winter' | 'allSeason';
  /**
   * The rim ID of the rim size that was automatically selected for the new
   * tire type in the Range Simulator. The old value can be accessed from
   * Range's `ctx.formState.rim`.
   */
  rimId: RangeSimRimId;
  /**
   * The rim dataset of the rim size that was automatically selected for the
   * new tire type in the Range Simulator. The old value can be
   * accessed from Range's `ctx.selectedRim`.
   */
  rim: RangeSimRimData;
}

export interface RangeRimSelectEventPayload {
  /**
   * The new rim ID of the rim size that was selected in the Range Simulator.
   * The old value can be accessed from Range's `ctx.formState.rim`.
   */
  rimId: RangeSimRimId;
  /**
   * The new rim dataset of the rim size that was selected in the Range
   * Simulator. The old value can be accessed from Range's `ctx.selectedRim`.
   */
  rim: RangeSimRimData;
}

export interface ChargeTimeTrimSelectEventPayload {
  /**
   * The trim dataset of the new trim that was selected in the ChargeTime
   * Simulator. The old value can be accessed from ChargeTime's
   * `ctx.selectedTrim`.
   */
  trim: BffTrimData<ChargeTimeTrimModel>;
  /**
   * The model dataset of the new model that was automatically selected by
   * changing the trim in the ChargeTime Simulator. The old value can be
   * accessed from ChargeTime's `ctx.selectedModel`.
   */
  model: ChargeTimeTrimModel;
}

export interface ChargeTimeEngineSelectEventPayload {
  /**
   * The model dataset of the new engine/model that was selected in the
   * ChargeTime Simulator. The old value can be accessed from ChargeTime's
   * `ctx.selectedModel`.
   */
  model: ChargeTimeTrimModel;
}

export interface CostTrimSelectEventPayload {
  /**
   * The trim dataset of the new trim that was selected in the Cost Simulator.
   * The old value can be accessed from Cost's `ctx.selectedTrim`.
   */
  trim: BffTrimData<CostTrimModel>;
  /**
   * The model dataset of the new model that was automatically selected by
   * changing the trim in the Cost Simulator. The old value can be accessed
   * from Cost's `ctx.selectedModel`.
   */
  model: CostTrimModel;
}

export interface CostEngineSelectEventPayload {
  /**
   * The model dataset of the new engine/model that was selected in the Cost
   * Simulator. The old value can be accessed from Cost's `ctx.selectedModel`.
   */
  model: CostTrimModel;
}
